// src/components/dashboard/DashboardTabs.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import {
    Rocket,
    Book,
    Users,
    Bot,
    Code
} from 'lucide-react';
import {
    fetchUserProfile,
    fetchUserAchievements,
    fetchUserBadges,
    fetchActiveSandboxes,
    fetchEnrollments,
    fetchComponentTypes
} from '../../services/api';
import { useDataFetching } from '../../hooks/useDataFetching';
import { useError } from '../../contexts/ErrorContext';
import ErrorBoundary from '../ErrorBoundary';
import DashboardMainTab from './tabs/DashboardMainTab';
import DashboardSandboxesTab from './tabs/DashboardSandboxesTab'
import DashboardAPITab from './tabs/DashboardAPITab';
import DashboardAITutorsTab from './tabs/DashboardAITutorsTab';
import DashboardGroupsTab from './tabs/DashboardGroupsTab';
import DashboardCoursesTab from './tabs/DashboardCoursesTab';

const DashboardTabs = ({ userInfo, token }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('dashboard');
    const { showError } = useError();

    // Use React.useCallback to memoize fetch functions and prevent infinite re-renders
    const fetchProfileData = React.useCallback(async () => {
        const response = await fetchUserProfile(token, userInfo.id);
        return Array.isArray(response) ? response : response;
    }, [token, userInfo?.id]);

    const fetchAchievementsData = React.useCallback(async () => {
        const response = await fetchUserAchievements(token);
        return Array.isArray(response) ? response : [];
    }, [token]);

    const fetchBadgesData = React.useCallback(async () => {
        const response = await fetchUserBadges(token);
        return Array.isArray(response) ? response : [];
    }, [token]);

    const fetchSandboxesData = React.useCallback(async () => {
        const response = await fetchActiveSandboxes(token, userInfo?.id, 'owner');
        return Array.isArray(response) ? response : [];
    }, [token, userInfo?.id]);

    const fetchEnrollmentsData = React.useCallback(async () => {
        const response = await fetchEnrollments(token);
        return Array.isArray(response) ? response : [];
    }, [token]);

    const fetchComponentTypesData = React.useCallback(async () => {
        const response = await fetchComponentTypes(token);
        return Array.isArray(response) ? response : [];
    }, [token]);

    // Now use these memoized functions in useDataFetching
    const { data: profile, isLoading: profileLoading } = useDataFetching(fetchProfileData, [token, userInfo?.id]);
    const { data: achievements, isLoading: achievementsLoading } = useDataFetching(fetchAchievementsData, [token]);
    const { data: badges, isLoading: badgesLoading } = useDataFetching(fetchBadgesData, [token]);
    const { data: mySandboxes, isLoading: sandboxesLoading, setData: setMySandboxes } = useDataFetching(fetchSandboxesData, [token, userInfo?.id]);
    const { data: enrollments, isLoading: enrollmentsLoading } = useDataFetching(fetchEnrollmentsData, [token]);
    const { data: componentTypes, isLoading: componentTypesLoading } = useDataFetching(fetchComponentTypesData, [token]);

    // Определяем общее состояние загрузки
    const isLoading = profileLoading || achievementsLoading || badgesLoading ||
        sandboxesLoading || enrollmentsLoading || componentTypesLoading;

    // Таймаут для предотвращения бесконечной загрузки
    const [loadingTimeout, setLoadingTimeout] = useState(false);

    useEffect(() => {
        // Если загрузка занимает более 15 секунд, форсируем рендер дашборда
        const timer = setTimeout(() => {
            if (isLoading) {
                console.warn('Loading timeout reached, forcing dashboard render');
                setLoadingTimeout(true);
            }
        }, 15000);

        return () => clearTimeout(timer);
    }, [isLoading]);

    // Получаем прогресс загрузки
    const calculateProgress = () => {
        let completed = 0;
        if (!profileLoading) completed += 1;
        if (!achievementsLoading) completed += 1;
        if (!badgesLoading) completed += 1;
        if (!sandboxesLoading) completed += 1;
        if (!enrollmentsLoading) completed += 1;
        if (!componentTypesLoading) completed += 1;
        return { completed, total: 6 };
    };

    const progress = calculateProgress();

    if (isLoading && !loadingTimeout) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <CircularProgress />
                    <div className="text-gray-500">
                        {t('dashboard.loading')}
                        {` (${progress.completed}/${progress.total})`}
                    </div>
                </div>
            </div>
        );
    }

    // Типизированная функция для отображения вкладок
    const renderActiveTab = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <ErrorBoundary>
                        <DashboardMainTab
                            userInfo={userInfo}
                            token={token}
                            setError={showError}
                            profile={profile}
                            achievements={achievements}
                            badges={badges}
                            mySandboxes={mySandboxes}
                            enrollments={enrollments}
                            componentTypes={componentTypes}
                            setMySandboxes={setMySandboxes}
                        />
                    </ErrorBoundary>
                );
            case 'sandboxes':
                return (
                    <DashboardSandboxesTab
                        userInfo={userInfo}
                        token={token}
                        setError={showError}
                        componentTypes={componentTypes || []}  // Передаем componentTypes
                        showError={showError}                  // Передаем функцию для показа ошибок
                    />
                );
            case 'courses':
                return (
                    <DashboardCoursesTab
                        token={token}
                        setError={showError}
                        profile={profile}
                    />
                );
            case 'ai-tutors':
                return (
                    <DashboardAITutorsTab
                        token={token}
                        setError={showError}
                    />
                );
            case 'groups':
                return (
                    <DashboardGroupsTab
                        userInfo={userInfo}
                        token={token}
                        setError={showError}
                    />
                );
            case 'api':
                return (
                    <DashboardAPITab
                        token={token}
                        setError={showError}
                    />
                );
            default:
                return <div>{t('dashboard.tabs.coming_soon')}</div>;
        }
    };

    return (
        <div className="min-h-screen w-full bg-gray-50">
            <div className="pt-4 pb-8">
                {/* Вкладки */}
                <div className="max-w-7xl mx-auto px-4">
                    <div className="border-b border-gray-200 mb-6">
                        <nav className="flex flex-wrap -mb-px space-x-2 md:space-x-4 lg:space-x-8">
                            <button
                                onClick={() => setActiveTab('dashboard')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'dashboard'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {t('dashboard.tabs.main')}
                            </button>
                            <button
                                onClick={() => setActiveTab('sandboxes')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'sandboxes'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                <span className="flex items-center gap-1">
                  <Rocket className="w-3 h-3 md:w-4 md:h-4" />
                    {t('dashboard.tabs.sandboxes')}
                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('courses')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'courses'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                <span className="flex items-center gap-1">
                  <Book className="w-3 h-3 md:w-4 md:h-4" />
                    {t('dashboard.tabs.courses')}
                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('ai-tutors')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'ai-tutors'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                <span className="flex items-center gap-1">
                  <Bot className="w-3 h-3 md:w-4 md:h-4" />
                    {t('dashboard.tabs.ai_tutors')}
                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('groups')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'groups'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3 md:w-4 md:h-4" />
                    {t('dashboard.tabs.groups')}
                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('api')}
                                className={`py-2 md:py-4 px-1 border-b-2 font-medium text-xs md:text-sm ${
                                    activeTab === 'api'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                <span className="flex items-center gap-1">
                  <Code className="w-3 h-3 md:w-4 md:h-4" />
                    {t('dashboard.tabs.api')}
                </span>
                            </button>
                        </nav>
                    </div>

                    {/* Содержимое вкладок */}
                    <div className="mb-8">
                        {renderActiveTab()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardTabs;